/**
 * questEngine.js — Evaluates quests against live app state.
 *
 * Pure functions — zero React.
 * Takes the quest config, the app state (s), and returns evaluated quest objects.
 */
import { QUESTS } from './questConfig';

/**
 * Evaluate all quests against current state.
 *
 * @param {object} s - app state
 * @param {array} proj - projection array (from calc.run)
 * @returns {array} evaluated quests, each with:
 *   ...original quest fields
 *   status: "locked" | "active" | "claimable" | "completed"
 *   progressValue: 0..1
 */
export function evaluateQuests(s, proj) {
  const completed = s.questsCompleted || {};
  const completedIds = new Set(Object.keys(completed));

  return QUESTS
    .slice()
    .sort((a, b) => a.order - b.order)
    .map((quest) => {
      // Already claimed by user
      if (completedIds.has(quest.id)) {
        return {
          ...quest,
          status: "completed",
          progressValue: 1,
          completedAt: completed[quest.id],
        };
      }

      // Prerequisite not yet completed
      if (quest.requires && !completedIds.has(quest.requires)) {
        return {
          ...quest,
          status: "locked",
          progressValue: 0,
        };
      }

      // Evaluate condition
      const met = quest.check(s, proj);
      const progressValue = quest.progress(s, proj);

      return {
        ...quest,
        status: met ? "claimable" : "active",
        progressValue: Math.max(0, Math.min(1, progressValue)),
      };
    });
}

/**
 * Get summary stats for display.
 *
 * @param {array} evaluated - result of evaluateQuests()
 * @returns {{ total, completed, active, claimable }}
 */
export function questStats(evaluated) {
  const total = evaluated.length;
  const completed = evaluated.filter((q) => q.status === "completed").length;
  const claimable = evaluated.filter((q) => q.status === "claimable").length;
  const active = evaluated.filter((q) => q.status === "active").length;
  return { total, completed, active, claimable };
}

/**
 * Mark a quest as completed.
 * Returns the updated questsCompleted object to merge into state.
 *
 * @param {object} current - s.questsCompleted (or {})
 * @param {string} questId
 * @returns {object} new questsCompleted
 */
export function claimQuest(current, questId) {
  return {
    ...(current || {}),
    [questId]: new Date().toISOString(),
  };
}
